import { useRef, useCallback } from "react";

function isAgGridCheckboxClick(event) {
  const target = event?.event?.target;
  if (!(target instanceof Element)) {
    return false;
  }
  return Boolean(
    target.closest(
      ".ag-checkbox-input-wrapper, .ag-selection-checkbox, input.ag-checkbox-input",
    ),
  );
}

function isInteractiveCellClick(event) {
  const target = event?.event?.target;
  if (!(target instanceof Element)) {
    return false;
  }
  return Boolean(target.closest("button, a, input, textarea, select"));
}

function hasNonEmptyTextSelection() {
  const sel = window.getSelection();
  return Boolean(sel && sel.type === "Range" && sel.toString().length > 0);
}

/** Pointer down position per grid — skip row toggle after drag-to-select text. */
const rowPointerDownByGrid = new WeakMap();

const ROW_CLICK_DRAG_THRESHOLD_PX = 5;

/** Click anywhere on the row to toggle selection; checkbox clicks are unchanged. */
export const pgGridRowClickToggleProps = {
  suppressRowClickSelection: true,
  onCellMouseDown: (event) => {
    const ev = event.event;
    if (ev && event.api && typeof ev.clientX === "number") {
      rowPointerDownByGrid.set(event.api, { x: ev.clientX, y: ev.clientY });
    }
  },
  onRowClicked: (event) => {
    if (isAgGridCheckboxClick(event)) {
      return;
    }
    if (isInteractiveCellClick(event)) {
      return;
    }

    const ev = event.event;
    const start = event.api ? rowPointerDownByGrid.get(event.api) : null;
    if (start && ev) {
      const dx = Math.abs(ev.clientX - start.x);
      const dy = Math.abs(ev.clientY - start.y);
      if (dx > ROW_CLICK_DRAG_THRESHOLD_PX || dy > ROW_CLICK_DRAG_THRESHOLD_PX) {
        return;
      }
    }
    if (event.api) {
      rowPointerDownByGrid.delete(event.api);
    }

    if (hasNonEmptyTextSelection()) {
      return;
    }

    event.node.setSelected(!event.node.isSelected());
  },
};

/**
 * AG Grid multi-select capped at `maxSelection` (keeps the newly selected row).
 * @param {number|null|undefined} maxSelection — when null/undefined, no cap
 */
export function useLimitedGridSelection(onSelectionChanged, maxSelection, getRowId) {
  const prevIdsRef = useRef([]);

  const onGridSelectionChanged = useCallback(
    (event) => {
      const api = event.api;
      let nodes = api.getSelectedNodes();
      const idOf = (row) => (getRowId ? getRowId(row) : row.id);

      if (
        maxSelection != null &&
        maxSelection > 0 &&
        nodes.length > maxSelection
      ) {
        const prev = new Set(prevIdsRef.current);
        const added = nodes.find((n) => !prev.has(idOf(n.data)));
        api.deselectAll();
        const keep = added || nodes[nodes.length - 1];
        if (keep) {
          keep.setSelected(true);
        }
        nodes = api.getSelectedNodes();
      }

      const rows = api.getSelectedRows();
      prevIdsRef.current = rows.map((r) => idOf(r));
      onSelectionChanged(rows);
    },
    [onSelectionChanged, maxSelection, getRowId],
  );

  return onGridSelectionChanged;
}
